import { supabase } from "../../lib/supabase";
import { getUserByAuthId, checkUserDocument } from "./usersService";

export const getUserOrders = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return [];

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) return [];

        const { data, error } = await supabase
          .from('orders')
          .select('*, order_items(*, product:products(*)), store:stores(*)')
          .eq('buyer_id', profile.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        const orders = data || [];
        const pmIds = Array.from(new Set(orders.map(o => o.payment_method_id).filter(Boolean)));
        if (pmIds.length > 0) {
            try {
                const { data: pms } = await supabase
                    .from('payment_methods')
                    .select('*')
                    .in('id', pmIds);
                if (pms) {
                    const pmsMap = new Map(pms.map((pm: any) => [pm.id, pm]));
                    orders.forEach(order => {
                        if (order.payment_method_id && pmsMap.has(order.payment_method_id)) {
                            order.payment_method = pmsMap.get(order.payment_method_id);
                        }
                    });
                }
            } catch (pmErr) {
                console.error('Error fetching payment methods in getUserOrders:', pmErr);
            }
        }
        return orders;
    } catch (error) {
        console.error('Error in getUserOrders:', error);
        return [];
    }
};

export const checkUserHasProductOrder = async (productId: number | string): Promise<{ hasOrder: boolean; order?: any }> => {
    try {
        if (!productId) return { hasOrder: false };
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return { hasOrder: false };

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) return { hasOrder: false };

        const { data, error } = await supabase
            .from('orders')
            .select('id, status, created_at, order_items(product_id)')
            .eq('buyer_id', profile.id)
            .order('created_at', { ascending: false });

        if (error || !data) {
            return { hasOrder: false };
        }

        const numPid = Number(productId);
        const existing = data.find((order: any) => {
            const active = !['cancelled', 'canceled', 'expired', 'refunded'].includes(order.status);
            if (!active) return false;
            return (order.order_items || []).some((item: any) => Number(item.product_id) === numPid);
        });

        if (existing) {
            return { hasOrder: true, order: existing };
        }

        return { hasOrder: false };
    } catch (err) {
        console.error('Error in checkUserHasProductOrder:', err);
        return { hasOrder: false };
    }
};

export const getUserOrderedProductIds = async (): Promise<number[]> => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return [];

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) return [];

        const { data, error } = await supabase
            .from('orders')
            .select('id, status, order_items(product_id)')
            .eq('buyer_id', profile.id)
            .order('created_at', { ascending: false });

        if (error || !data) return [];

        const productIds = new Set<number>();
        data.forEach((order: any) => {
            if (!['cancelled', 'canceled', 'expired', 'refunded'].includes(order.status)) {
                (order.order_items || []).forEach((item: any) => {
                    if (item.product_id) {
                        productIds.add(Number(item.product_id));
                    }
                });
            }
        });

        return Array.from(productIds);
    } catch (err) {
        console.error('Error in getUserOrderedProductIds:', err);
        return [];
    }
};


export const getReceivedOrders = async (storeId?: number | string) => {
    try {
        let targetStoreId = storeId ? Number(storeId) : null;

        if (!targetStoreId) {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) return [];

            const profile = await getUserByAuthId(session.user.id);
            if (!profile) return [];

            if (profile.store_id) {
                targetStoreId = Number(profile.store_id);
            } else {
                const { data: store } = await supabase
                  .from('stores')
                  .select('id, store_members!inner(user_id)')
                  .eq('store_members.user_id', profile.id)
                  .maybeSingle();
                targetStoreId = store?.id ? Number(store.id) : null;
            }
        }

        if (!targetStoreId) return [];

        const { data, error } = await supabase
          .from('orders')
          .select('*, order_items(*, product:products(*)), buyer:users!fk_order_buyer(*)')
          .eq('store_id', targetStoreId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        const orders = data || [];
        const buyerIds = Array.from(new Set(orders.map(o => o.buyer_id).filter(Boolean)));
        
        if (buyerIds.length > 0) {
            try {
                const numericBuyerIds = buyerIds.map(id => Number(id)).filter(id => !isNaN(id));
                const query = (supabase.rpc('get_safe_users') as any)
                    .select('id, username, fullname, avatar');
                
                const { data: buyers, error: buyersErr } = await (numericBuyerIds.length > 0 
                    ? query.in('id', numericBuyerIds) 
                    : query.in('id', buyerIds));
                
                if (buyers && !buyersErr) {
                    const buyersMap = new Map();
                    buyers.forEach((b: any) => {
                        buyersMap.set(b.id, b);
                        buyersMap.set(Number(b.id), b);
                        buyersMap.set(String(b.id), b);
                    });
                    orders.forEach(order => {
                        const matched = buyersMap.get(order.buyer_id) || buyersMap.get(Number(order.buyer_id)) || buyersMap.get(String(order.buyer_id));
                        if (matched) {
                            order.buyer = { ...(order.buyer || {}), ...matched };
                        }
                    });
                }
            } catch (err) {
                console.error('Error fetching buyers in getReceivedOrders:', err);
            }
        }

        const pmIds = Array.from(new Set(orders.map(o => o.payment_method_id).filter(Boolean)));
        if (pmIds.length > 0) {
            try {
                const { data: pms } = await supabase
                    .from('payment_methods')
                    .select('*')
                    .in('id', pmIds);
                if (pms) {
                    const pmsMap = new Map(pms.map((pm: any) => [pm.id, pm]));
                    orders.forEach(order => {
                        if (order.payment_method_id && pmsMap.has(order.payment_method_id)) {
                            order.payment_method = pmsMap.get(order.payment_method_id);
                        }
                    });
                }
            } catch (pmErr) {
                console.error('Error fetching payment methods in getReceivedOrders:', pmErr);
            }
        }

        return orders;
    } catch (error) {
        console.error('Error in getReceivedOrders:', error);
        return [];
    }
};


export const getOrderDetails = async (orderId: string) => {
    try {
        const { data, error } = await supabase
          .from('orders')
          .select('*, order_items(*, product:products(*)), store:stores!fk_orders_store(*), buyer:users!fk_order_buyer(*), shipping_method:shipping_methods!fk_order_shipping_method(*), address:user_addresses!fk_order_address(*)')
          .eq('id', orderId)
          .maybeSingle();

        if (error) throw error;
        
        if (data && data.payment_method_id) {
            try {
                const { data: pmData } = await supabase
                    .from('payment_methods')
                    .select('*')
                    .eq('id', data.payment_method_id)
                    .maybeSingle();
                if (pmData) {
                    data.payment_method = pmData;
                }
            } catch (pmErr) {
                console.error('Error fetching order payment method details:', pmErr);
            }
        }

        if (data && data.buyer_id && (!data.buyer || !data.buyer.username)) {
            try {
                const numericBuyerId = Number(data.buyer_id);
                const { data: buyerData, error: buyerErr } = await (supabase.rpc('get_safe_users') as any)
                    .select('id, username, fullname, avatar')
                    .eq('id', !isNaN(numericBuyerId) ? numericBuyerId : data.buyer_id)
                    .maybeSingle();
                if (buyerData && !buyerErr) {
                    data.buyer = { ...(data.buyer || {}), ...buyerData };
                }
            } catch (err) {
                console.error('Error fetching buyer in getOrderDetails:', err);
            }
        }
        
        return data;
    } catch (error) {
        console.error('Error in getOrderDetails:', error);
        return null;
    }
};


export const updateOrderStatus = async (orderId: string, status: string) => {
    try {
        // Use the new public.store_update_order_status RPC
        const { data, error } = await supabase.rpc('store_update_order_status', {
            p_order_id: Number(orderId),
            p_new_status: status
        });

        if (error) {
            console.error('Error calling store_update_order_status RPC:', error);
            throw error;
        }
        return { ok: true, data };
    } catch (error: any) {
        console.error('Error in updateOrderStatus:', error);
        return { ok: false, error: error?.message || String(error) };
    }
};


export const cleanupExpiredOrders = async () => {
    try {
        // Fetch pending/waiting_payment orders with store expiration config
        const { data: activeOrders, error: fetchError } = await supabase
          .from('orders')
          .select('id, created_at, expires_at, store_id, stores(order_expiration_hours)')
          .in('status', ['pending_payment', 'waiting_payment']);

        if (fetchError) throw fetchError;

        const expiredOrderIds: number[] = [];
        const now = Date.now();

        if (activeOrders) {
          for (const order of activeOrders) {
            if (order.expires_at) {
              if (new Date(order.expires_at).getTime() < now) {
                expiredOrderIds.push(order.id);
              }
            } else {
              // Fallback: calculate using store config or default to 72 hours
              const storeConfig = order.stores as any;
              const expirationHours = storeConfig?.order_expiration_hours !== undefined 
                ? storeConfig.order_expiration_hours 
                : 72;
              const createdAtTime = new Date(order.created_at).getTime();
              if (createdAtTime + expirationHours * 60 * 60 * 1000 < now) {
                expiredOrderIds.push(order.id);
              }
            }
          }
        }

        if (expiredOrderIds.length > 0) {
          const { error: updateError } = await supabase
            .from('orders')
            .update({ status: 'expired' })
            .in('id', expiredOrderIds);
          
          if (updateError) throw updateError;
        }

        return { ok: true, count: expiredOrderIds.length };
    } catch (error) {
        console.error('Error in cleanupExpiredOrders:', error);
        return { ok: false };
    }
};


export const checkout = async (items: any[], addressId?: string | number) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Usuário não autenticado');

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) throw new Error('Perfil não encontrado');

    // VERIFICATION: Check if user has document registered
    const doc = await checkUserDocument(profile.id);
    if (!doc) {
      throw new Error('DOCUMENT_REQUIRED');
    }

    // VERIFICATION: Check if user already has an active order for any tournament ticket product in items
    const checkoutProductIds = items.map(i => Number(i.id || i.product_id)).filter(Boolean);
    if (checkoutProductIds.length > 0) {
      const { data: ticketMatches } = await supabase
        .from('products')
        .select('id')
        .in('id', checkoutProductIds)
        .eq('product_type_id', 52);

      if (ticketMatches && ticketMatches.length > 0) {
        const ticketProdIds = ticketMatches.map(t => Number(t.id));
        const { data: existingOrders } = await supabase
          .from('orders')
          .select('id, status, order_items(product_id)')
          .eq('buyer_id', profile.id);

        const hasAlready = (existingOrders || []).some(o => 
          !['cancelled', 'canceled', 'expired', 'refunded'].includes(o.status) &&
          (o.order_items || []).some((oi: any) => ticketProdIds.includes(Number(oi.product_id)))
        );

        if (hasAlready) {
          throw new Error('Você já possui um pedido para o ingresso deste torneio.');
        }
      }
    }

    // Group items by store
    const itemsByStore: Record<number, any[]> = {};
    items.forEach(item => {
      const sid = item.storeId || item.store_id;
      if (!itemsByStore[sid]) itemsByStore[sid] = [];
      itemsByStore[sid].push(item);
    });

    const storeIds = Object.keys(itemsByStore).map(Number);
    const { data: storesData } = await supabase
      .from('stores')
      .select('id, order_expiration_hours')
      .in('id', storeIds);
    
    const storeExpirations = (storesData || []).reduce((acc, s) => {
      acc[s.id] = s.order_expiration_hours;
      return acc;
    }, {} as Record<number, number>);

    const ordersCreated = [];

    for (const storeId in itemsByStore) {
      const storeItems = itemsByStore[storeId];
      const totalAmount = storeItems.reduce((acc, i) => acc + (i.price * i.quantity), 0);
      const expirationHours = storeExpirations[Number(storeId)] || 72;
      const expiresAt = new Date(Date.now() + expirationHours * 60 * 60 * 1000).toISOString();

      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          buyer_id: profile.id,
          store_id: Number(storeId),
          shipping_address_id: addressId ? Number(addressId) : null,
          products_total: totalAmount,
          amount: totalAmount,
          status: 'pending_payment',
          expires_at: expiresAt
        })
        .select()
        .maybeSingle();

      if (orderError) throw orderError;
      if (!order) throw new Error('Falha ao criar pedido');

      const orderItems = storeItems.map(i => ({
        order_id: order.id,
        product_id: i.id,
        quantity: i.quantity,
        unit_price: i.price
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;
      ordersCreated.push(order);
    }

    return { orders: ordersCreated, success: true };
  } catch (error: any) {
    console.error('Error during checkout:', error);
    throw error;
  }
};


export const createOrderFull = async (params: {
    store_id: number;
    address_id?: number | null;
    shipping_method_id?: number;
    payment_method_id: number | string;
    shipping_cost?: number; // Note: RPC might handle this differently or calculate it
    items: { product_id: number; quantity: number, price: number }[];
}) => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error('Login necessário');

        const profile = await getUserByAuthId(session.user.id);
        if (!profile) throw new Error('Perfil não encontrado');

        // VERIFICATION: Check if store sales are enabled
        const { data: storeInfo, error: storeErr } = await supabase
            .from('stores')
            .select('sales_enabled, name, order_expiration_hours')
            .eq('id', params.store_id)
            .maybeSingle();

        if (storeErr) throw storeErr;
        if (!storeInfo) throw new Error('Loja não encontrada');
        
        // Store owners can place internal pickup/counter orders even if sales are temporarily disabled
        const isStoreOwner = Number(profile.store_id) === Number(params.store_id);
        if (storeInfo.sales_enabled === false && !isStoreOwner) {
            throw new Error(`As vendas estão desabilitadas para a loja "${storeInfo.name || ''}".`);
        }

        // VERIFICATION: Check if user has document registered
        if (!isStoreOwner) {
          const doc = await checkUserDocument(profile.id);
          if (!doc) {
            throw new Error('DOCUMENT_REQUIRED');
          }
        }

        // VERIFICATION: Check if user already has an active order for any tournament ticket product in items
        const orderProductIds = params.items.map(i => Number(i.product_id)).filter(Boolean);
        if (orderProductIds.length > 0) {
          const { data: ticketMatches } = await supabase
            .from('products')
            .select('id')
            .in('id', orderProductIds)
            .eq('product_type_id', 52);

          if (ticketMatches && ticketMatches.length > 0) {
            const ticketProdIds = ticketMatches.map(t => Number(t.id));
            const { data: existingOrders } = await supabase
              .from('orders')
              .select('id, status, order_items(product_id)')
              .eq('buyer_id', profile.id);

            const hasAlready = (existingOrders || []).some(o => 
              !['cancelled', 'canceled', 'expired', 'refunded'].includes(o.status) &&
              (o.order_items || []).some((oi: any) => ticketProdIds.includes(Number(oi.product_id)))
            );

            if (hasAlready) {
              throw new Error('Você já possui um pedido para o ingresso deste torneio.');
            }
          }
        }

        const payload = {
            p_buyer_id: Number(profile.id),
            p_store_id: Number(params.store_id),
            p_address_id: params.address_id ? Number(params.address_id) : null,
            p_shipping_method_id: params.shipping_method_id ? Number(params.shipping_method_id) : 4,
            p_payment_method_id: Number(params.payment_method_id),
            p_items: params.items.map(i => ({
                product_id: Number(i.product_id),
                quantity: Number(i.quantity)
            }))
        };

        // Execute secure database-side transaction using public.create_order_full RPC
        const { data: orderId, error: rpcError } = await supabase.rpc('create_order_full', payload);

        if (rpcError) throw rpcError;
        if (!orderId) throw new Error('Falha ao obter ID do pedido da RPC');

        if (params.shipping_cost !== undefined && orderId) {
            await supabase.from('orders').update({ shipping_cost: Number(params.shipping_cost) || 0 }).eq('id', orderId);
        }

        // Fetch the newly created order details to return the complete object
        const { data: order, error: orderError } = await supabase
            .from('orders')
            .select('*')
            .eq('id', orderId)
            .maybeSingle();

        if (orderError) throw orderError;
        if (!order) throw new Error('Pedido criado não pôde ser recuperado');

        return order;
    } catch (error: any) {
        console.error('Error in createOrderFull:', error);
        if (error?.message?.includes('tournament_tickets') || (error?.code === '42P01' && String(error?.message).includes('tournament_tickets'))) {
            const dbError = new Error('Erro no banco de dados (Supabase): Os triggers legados na tabela order_items ainda tentam atualizar a tabela descontinuada "tournament_tickets". Por favor, execute o script SQL de remoção dos triggers (supabase_fix_order_items_triggers.sql) no SQL Editor do Supabase.');
            (dbError as any).code = '42P01';
            (dbError as any).originalError = error;
            throw dbError;
        }
        throw error;
    }
};


